import { useEffect } from 'react';
import { useRouter } from 'next/router';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';

// Utils
import {
    getLocalStorageLanguage,
    setLocalStorageLanguage
} from '@/utils/localStorage/languageLocalStorage';

// Types
import type { PlatformLanguagesType } from '@/types/userType';

const useLanguage = () => {
    const router = useRouter();

    // Custom setLanguage replacement using Next.js router
    const setLanguage = async (locale: string) => {
        await router.push(router.asPath, router.asPath, { locale });
    };

    // Makes sure that the right language is used
    const useSetLanguage = () => {
        const { user } = useUser();
        const platformLanguage = user?.settings?.platform_language;
        const currentLang = router.locale;

        useEffect(() => {
            const localStorageLanguage = getLocalStorageLanguage();

            if (!localStorageLanguage) {
                setLocalStorageLanguage('en');
            }
            if (platformLanguage) {
                if (platformLanguage !== localStorageLanguage) {
                    setLocalStorageLanguage(platformLanguage);
                    setLanguage(platformLanguage);
                } else if (
                    platformLanguage === localStorageLanguage &&
                    currentLang !== localStorageLanguage
                ) {
                    setLanguage(platformLanguage);
                }
            }
        }, [platformLanguage]);
    };

    const setLanguageLocalStorage = (platformLanguage: PlatformLanguagesType) => {
        const localStorageLanguage = getLocalStorageLanguage();

        if (!localStorageLanguage) {
            setLocalStorageLanguage(platformLanguage);
        }

        if (platformLanguage !== localStorageLanguage) {
            setLocalStorageLanguage(platformLanguage);
            setLanguage(platformLanguage);
        }
    };
    return { useSetLanguage, setLanguageLocalStorage };
};

export default useLanguage;
